const keyBy = require('lodash/keyBy')
const shipsyFetch = require('./fetch')
const {
  generatePurchaseOrder,
  generateSalesOrder,
  transformProducts,
  groupProductsByWarehouse,
  generateStockTransferOrder,
  generateReturnShipment } = require('../tools')

class ShipsyAdapter {
  constructor (shipsyApiConfigs = {}, fetch, logger, api) {
    if (!shipsyApiConfigs || !shipsyApiConfigs.apiKey) {
      // If no configs provided, skip
      return
    }
    this.logger = logger
    this._rawFetch = fetch
    this.apiToken = shipsyApiConfigs.apiKey
    this.api = api
    this.baseUrl = shipsyApiConfigs.baseUrl
    this.workspace = shipsyApiConfigs.workspace
    this.paths = shipsyApiConfigs

    if (typeof fetch !== 'function') {
      throw new Error('fetch is expected as an argument')
    }
  }

  async getAuthedFetch () {
    // Build a fresh authed fetch with the current token
    if (!this.apiToken) {
      this.logger.warn('ShipsyAdapter: no api token provided')
    }
    return shipsyFetch({
      token: this.apiToken,
      fetch: this._rawFetch,
      logger: this.logger,
      baseUrl: this.baseUrl,
      workspace: this.workspace
    })
  }

  getPath (key) {
    const value = this.paths[key]
    if (!value) {
      throw new Error(`ShipsyAdapter: missing path for ${key}`)
    }
    return value
  }

  async upsertProducts (products) {
    if (!Array.isArray(products)) products = [products]

    const centralWarehouses = await this.api.shipment.dataShipmentAdapter.getCentralWarehouses()

    const centralWarehousesByMarketCode = keyBy(centralWarehouses, 'productMarketCode')

    const transformedProducts = transformProducts(products, centralWarehousesByMarketCode)

    if (!transformedProducts.length) {
      this.logger.info('No products to upsert to Shipsy')
      return
    }

    const path = this.getPath('upsertProducts')

    const authedFetch = await this.getAuthedFetch()

    const batches = groupProductsByWarehouse(transformedProducts, 100) // Shipsy has a batchsize limit of 100
    for (const batch of batches) {
      await authedFetch({
        path,
        body: batch,
        method: 'POST'
      })
    }
  }

  async createPurchaseOrder (order, locationUuidById) {
    const transformedOrder = generatePurchaseOrder(order, locationUuidById)
    const path = this.getPath('createPurchaseOrder')

    const authedFetch = await this.getAuthedFetch()

    await authedFetch({
      path,
      body: transformedOrder,
      method: 'POST'
    })
  }

  async updatePurchaseOrder (order, locationUuidById) {
    const transformedOrder = generatePurchaseOrder(order, locationUuidById)
    const path = this.getPath('updatePurchaseOrder')

    const authedFetch = await this.getAuthedFetch()

    await authedFetch({
      path,
      body: transformedOrder,
      method: 'POST'
    })
  }

  async createSalesOrder (shipment, locationUuidById) {
    const transformedOrder = generateSalesOrder(shipment, locationUuidById)
    const path = this.getPath('createSalesOrder')

    const authedFetch = await this.getAuthedFetch()

    await authedFetch({
      path,
      body: transformedOrder,
      method: 'POST'
    })
  }

  async updateSalesOrder (shipment, locationUuidById) {
    const transformedOrder = generateSalesOrder(shipment, locationUuidById)
    const path = this.getPath('updateSalesOrder')

    const authedFetch = await this.getAuthedFetch()

    await authedFetch({
      path,
      body: transformedOrder,
      method: 'POST'
    })
  }

  async createReturnShipment (shipment, locationById) {
    const batchDocs = await this._fetchBatchDocs(shipment.counts)
    const transformedShipment = generateReturnShipment(shipment, locationById, batchDocs)
    const path = this.getPath('createReturnShipment')

    const authedFetch = await this.getAuthedFetch()

    await authedFetch({
      path,
      body: transformedShipment,
      method: 'POST'
    })
  }

  async createStockTransferOrder (shipment, locationById) {
    const transformedShipment = generateStockTransferOrder(shipment, locationById)
    const path = this.getPath('createStockTransferOrder')

    const authedFetch = await this.getAuthedFetch()

    await authedFetch({
      path,
      body: transformedShipment,
      method: 'POST'
    })
  }

  async updateStockTransferOrder (shipment, locationById) {
    const transformedShipment = generateStockTransferOrder(shipment, locationById)
    const path = this.getPath('updateStockTransferOrder')

    const authedFetch = await this.getAuthedFetch()

    await authedFetch({
      path,
      body: transformedShipment,
      method: 'POST'
    })
  }

  // Fetch batch documents from master-data for the batchIds present on the shipment.
  async _fetchBatchDocs (counts = {}) {
    const batchIds = Object.values(counts)
      .flatMap(entry => entry.batches || [])
      .map(batch => batch.batchId)
      .filter(Boolean)

    if (batchIds.length === 0) return {}

    try {
      const uniqueIds = [...new Set(batchIds)]
      const docs = await this.api.batch.list({ ids: uniqueIds })
      return keyBy(docs, '_id')
    } catch (err) {
      this.logger.warn({ err }, 'ShipsyAdapter: failed to fetch batch docs for return shipment')
      return {}
    }
  }
}

module.exports = ShipsyAdapter
